import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar as CalendarIcon, Plus, Trash2, Video, Clock, Users, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';

interface WebinarSession {
  id: string;
  course_id: string;
  session_date: string;
  duration_minutes: number;
  title: string;
  description?: string;
  meeting_url?: string;
  meeting_platform?: string;
  meeting_id?: string;
  meeting_passcode?: string;
  max_participants?: number;
}

interface WebinarSchedulerProps {
  courseId: string;
  meetingPlatform?: string;
  meetingUrl?: string;
  meetingId?: string;
  meetingPasscode?: string;
}

export function WebinarScheduler({
  courseId,
  meetingPlatform,
  meetingUrl,
  meetingId,
  meetingPasscode
}: WebinarSchedulerProps) {
  const [sessions, setSessions] = useState<WebinarSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [newSession, setNewSession] = useState({
    title: '',
    description: '',
    time: '09:00',
    duration_minutes: 60,
    max_participants: 0
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchSessions();
  }, [courseId]);

  const fetchSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('webinar_sessions')
        .select('*')
        .eq('course_id', courseId)
        .order('session_date', { ascending: true });

      if (error) throw error;
      setSessions(data || []);
    } catch (error) {
      console.error('Error fetching webinar sessions:', error);
      toast({
        title: "Error",
        description: "Failed to load webinar sessions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSession = async () => {
    if (!selectedDate || !newSession.title) {
      toast({
        title: "Validation Error",
        description: "Please select a date and provide a title",
        variant: "destructive"
      });
      return;
    }

    try {
      // Combine date and time
      const [hours, minutes] = newSession.time.split(':');
      const sessionDate = new Date(selectedDate);
      sessionDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      const { error } = await supabase
        .from('webinar_sessions')
        .insert({
          course_id: courseId,
          session_date: sessionDate.toISOString(),
          duration_minutes: newSession.duration_minutes,
          title: newSession.title,
          description: newSession.description || null,
          meeting_url: meetingUrl || null,
          meeting_platform: meetingPlatform || null,
          meeting_id: meetingId || null,
          meeting_passcode: meetingPasscode || null,
          max_participants: newSession.max_participants > 0 ? newSession.max_participants : null
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Webinar session scheduled successfully"
      });

      setShowAddDialog(false);
      setNewSession({
        title: '',
        description: '',
        time: '09:00',
        duration_minutes: 60,
        max_participants: 0
      });
      setSelectedDate(undefined);
      fetchSessions();
    } catch (error) {
      console.error('Error adding webinar session:', error);
      toast({
        title: "Error",
        description: "Failed to schedule webinar session",
        variant: "destructive"
      });
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      const { error } = await supabase
        .from('webinar_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Webinar session deleted"
      });
      fetchSessions();
    } catch (error) {
      console.error('Error deleting webinar session:', error);
      toast({
        title: "Error",
        description: "Failed to delete webinar session",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return <div>Loading webinar sessions...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Scheduled Webinar Sessions
            </CardTitle>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Schedule Session
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Schedule New Webinar Session</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Select Date</Label>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => date < new Date()}
                        className="rounded-md border pointer-events-auto"
                      />
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="session-title">Session Title *</Label>
                        <Input
                          id="session-title"
                          value={newSession.title}
                          onChange={(e) => setNewSession({ ...newSession, title: e.target.value })}
                          placeholder="e.g., Week 1: Introduction"
                        />
                      </div>
                      <div>
                        <Label htmlFor="session-time">Start Time *</Label>
                        <Input
                          id="session-time"
                          type="time"
                          value={newSession.time}
                          onChange={(e) => setNewSession({ ...newSession, time: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="duration">Duration (minutes)</Label>
                        <Input
                          id="duration"
                          type="number"
                          value={newSession.duration_minutes}
                          onChange={(e) => setNewSession({ ...newSession, duration_minutes: parseInt(e.target.value) })}
                          min={15}
                        />
                      </div>
                      <div>
                        <Label htmlFor="max-participants">Max Participants (optional)</Label>
                        <Input
                          id="max-participants"
                          type="number"
                          value={newSession.max_participants}
                          onChange={(e) => setNewSession({ ...newSession, max_participants: parseInt(e.target.value) })}
                          placeholder="Leave 0 for unlimited"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="session-description">Description (optional)</Label>
                    <Textarea
                      id="session-description"
                      value={newSession.description}
                      onChange={(e) => setNewSession({ ...newSession, description: e.target.value })}
                      placeholder="What will be covered in this session..."
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddSession}>
                      Schedule Session
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CalendarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No webinar sessions scheduled yet</p>
              <p className="text-sm mt-2">Click "Schedule Session" to add your first live webinar</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <Card key={session.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          <Video className="h-4 w-4 text-primary" />
                          <h4 className="font-semibold">{session.title}</h4>
                        </div>
                        {session.description && (
                          <p className="text-sm text-muted-foreground">{session.description}</p>
                        )}
                        <div className="flex flex-wrap gap-3 text-sm">
                          <Badge variant="outline" className="flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" />
                            {format(new Date(session.session_date), 'PPP')}
                          </Badge>
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(new Date(session.session_date), 'p')} ({session.duration_minutes} min)
                          </Badge>
                          {session.max_participants && (
                            <Badge variant="outline" className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              Max: {session.max_participants}
                            </Badge>
                          )}
                          {session.meeting_platform && (
                            <Badge variant="secondary">
                              {session.meeting_platform === 'zoom' && 'Zoom'}
                              {session.meeting_platform === 'teams' && 'Teams'}
                              {session.meeting_platform === 'google_meet' && 'Google Meet'}
                            </Badge>
                          )}
                        </div>
                        {session.meeting_url && (
                          <a
                            href={session.meeting_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Join Meeting Link
                          </a>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSession(session.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {!meetingPlatform || meetingPlatform === 'none' ? (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <p className="text-sm text-amber-800">
              <strong>Tip:</strong> Configure your meeting platform in the Course Settings tab to automatically populate meeting links for all scheduled sessions.
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}